import React, { useState, useRef, useEffect } from 'react';
import { useEditor } from '../../context/EditorContext';
import { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuCheckboxItem, ContextMenuItem, ContextMenuSeparator, ContextMenuSub, ContextMenuSubTrigger, ContextMenuSubContent, ContextMenuRadioGroup, ContextMenuRadioItem } from '../ui/context-menu';
import { Trash2 } from 'lucide-react';
import { PANEL_GUTTERS, DEFAULT_PANEL_GUTTER, gutterLabel } from '../../lib/foldPresets';
import { snapGuidePosition } from '../../lib/guideSnap';

/**
 * Panel-margin guide (derived from a fold line's gutter — see lib/foldPresets.js): a thin dashed line on either side of the
 * crease that objects snap to. Not draggable; change it from the fold line's right-click menu or Page Design ▸ Guides ▸ Fold Lines.
 */
const PanelGuideLine = ({ guide, zoom, label }) => {
  const isHorizontal = guide.orientation === 'horizontal';
  const position = guide.position * zoom;
  return (
    <div
      className={`guide-line ${guide.kind} ${guide.orientation}`}
      title={label}
      data-kind={guide.kind}
      data-fold-id={guide.foldId}
      aria-hidden="true"
      style={{ zIndex: 998, ...(isHorizontal ? { top: position - 4 } : { left: position - 4 }) }}
      data-testid={`guide-${guide.id}`}
    >
      <div className="guide-line-core" />
    </div>
  );
};

export const GuideLine = ({ guide, zoom, pageWidth, pageHeight }) => {
  const { dispatch, rawDispatch, pxToUnit, state, stateRef } = useEditor();
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState(guide.position);
  const lineRef = useRef(null);
  
  const isHorizontal = guide.orientation === 'horizontal';
  // Fold lines (brochure panels) print as dashed fold marks when "Fold marks" is on in Export / Print
  const isFold = guide.kind === 'fold';
  const gutter = Number(guide.gutter) || 0;
  const send = rawDispatch || dispatch;
  // Turning a guide into a fold line gives it Publisher's default panel margins; turning it back clears them.
  const toggleFold = () => send({ type: 'UPDATE_GUIDE', payload: { id: guide.id, data: isFold ? { kind: undefined, gutter: undefined } : { kind: 'fold', gutter: DEFAULT_PANEL_GUTTER } } });
  const setGutter = (v) => send({ type: 'UPDATE_GUIDE', payload: { id: guide.id, data: { gutter: Number(v) } } });
  const unit = state?.canvasUnit || 'in';
  const fmt = (px) => `${pxToUnit ? pxToUnit(px) : Math.round(px)} ${unit}`;
  const posLabel = fmt(guide.position);
  const gutterText = gutterLabel(gutter);

  useEffect(() => {
    setDragPosition(guide.position);
  }, [guide.position]);

  if (guide.kind === 'panel') {
    return <PanelGuideLine guide={guide} zoom={zoom} label={`Panel margin · ${fmt(guide.position)} (${gutterLabel(guide.gutter)} from the fold)`} />;
  }
  if (guide.kind === 'center') {
    return <PanelGuideLine guide={guide} zoom={zoom} label={`Panel centre · ${fmt(guide.position)} (panel ${guide.panel} of ${guide.count})`} />;
  }
  
  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // right-click opens the guide menu
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  /**
   * Where the guide lands for this mouse position: snapped to the page centre / edges / other guides when close, else to
   * the ruler tick — Alt drags freely (lib/guideSnap). `along` is the cursor's position along the guide, for the tooltip.
   */
  const resolve = (e) => {
    const canvas = lineRef.current?.closest('.canvas-page');
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const raw = isHorizontal ? (e.clientY - rect.top) / zoom : (e.clientX - rect.left) / zoom;
    const along = isHorizontal ? (e.clientX - rect.left) / zoom : (e.clientY - rect.top) / zoom;
    const st = stateRef?.current || state;
    const { position, snap } = snapGuidePosition(raw, { orientation: guide.orientation, guides: st.guides, pageSize: isHorizontal ? pageHeight : pageWidth, unit: st.canvasUnit, zoom, skipId: guide.id, free: e.altKey });
    return { raw, position, snap, along };
  };

  const handleMouseMove = (e) => {
    const r = resolve(e);
    if (!r) return;
    setDragPosition(r.position);
    send({ type: 'SET_GUIDE_DRAG', payload: { id: guide.id, orientation: guide.orientation, position: r.position, along: r.along, snap: r.snap, preview: false } });
  };
  
  const handleMouseUp = (e) => {
    const r = resolve(e);
    send({ type: 'SET_GUIDE_DRAG', payload: null });
    if (!r) return;
    // Check if guide is dragged outside the canvas - delete it
    const maxBound = isHorizontal ? pageHeight : pageWidth;
    if (r.raw < -20 || r.raw > maxBound + 20) {
      dispatch({ type: 'DELETE_GUIDE', payload: guide.id });
    } else {
      // Clamp position to canvas bounds
      const clampedPosition = Math.max(0, Math.min(r.position, maxBound));
      dispatch({
        type: 'UPDATE_GUIDE',
        payload: { id: guide.id, data: { position: clampedPosition } }
      });
    }
    
    setIsDragging(false);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };
  
  const handleDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch({ type: 'DELETE_GUIDE', payload: guide.id });
  };
  
  // Guide positions are stored in canvas px; the page container is not CSS-scaled, so place them at zoomed coordinates.
  const position = (isDragging ? dragPosition : guide.position) * zoom;
  
  return (
    <ContextMenu>
    <ContextMenuTrigger asChild>
    <div
      ref={lineRef}
      className={`guide-line ${guide.orientation} ${isDragging ? 'dragging' : ''} ${isFold ? 'fold' : ''}`}
      title={`${isFold ? `Fold line · ${posLabel}${gutter ? ` · panel margins ${gutterText}` : ''}` : guide.kind === 'margin' ? `Margin guide · ${posLabel}` : `Guide · ${posLabel}`} — drag to move (snaps to the ruler ticks, page centre, margins and other guides; Alt: free), double-click to remove`}
      data-kind={guide.kind || 'guide'}
      // 9 px hit area centred on the line; the visible 1 px (2 px while dragging) core is the .guide-line-core child (index.css)
      style={{ zIndex: 999, cursor: isHorizontal ? 'ns-resize' : 'ew-resize', ...(isHorizontal ? { top: position - 4 } : { left: position - 4 }) }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      data-testid={`guide-${guide.id}`}
    >
      <div className="guide-line-core" aria-hidden="true" />
      {/* the exact position while dragging is shown by GuideDragOverlay (CanvasWorkspace), next to the cursor */}
    </div>
    </ContextMenuTrigger>
    <ContextMenuContent className="w-56" data-testid={`guide-menu-${guide.id}`}>
      <ContextMenuCheckboxItem checked={isFold} onCheckedChange={toggleFold} data-testid={`guide-menu-fold-${guide.id}`}>
        Fold line
        <span className="ml-auto text-[11px] text-muted-foreground">prints as fold mark</span>
      </ContextMenuCheckboxItem>
      {isFold && (
        <ContextMenuSub>
          <ContextMenuSubTrigger data-testid={`guide-menu-panel-trigger-${guide.id}`}>Panel margins<span className="ml-auto text-[11px] text-muted-foreground">{gutterText}</span></ContextMenuSubTrigger>
          <ContextMenuSubContent className="w-56" data-testid={`guide-menu-panel-content-${guide.id}`}>
            <div className="px-2 py-1.5 text-[11px] text-muted-foreground">Dashed guides on both sides of the crease — objects snap to them so text stays clear of the fold.</div>
            <ContextMenuRadioGroup value={String(gutter)} onValueChange={setGutter}>
              {PANEL_GUTTERS.map((o) => <ContextMenuRadioItem key={o.value} value={String(o.value)} data-testid={`guide-menu-panel-${guide.id}-${o.value}`}>{o.label}</ContextMenuRadioItem>)}
            </ContextMenuRadioGroup>
          </ContextMenuSubContent>
        </ContextMenuSub>
      )}
      <ContextMenuSeparator />
      <ContextMenuItem onSelect={() => dispatch({ type: 'DELETE_GUIDE', payload: guide.id })} data-testid={`guide-menu-delete-${guide.id}`}>
        <Trash2 className="h-4 w-4 mr-2" /> Delete guide
      </ContextMenuItem>
    </ContextMenuContent>
    </ContextMenu>
  );
};
